import React from "react";
import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { AppColors } from "../../../../../util/AppColors";
import { FaWhatsapp } from "react-icons/fa";

const TAB_ORDER = ["internal",
  //  "external"
  ];

const TAB_LABELS = {
  internal: "Internal",
  // external: "External",
};

const ChatSidebar = ({
  chats,
  doctors = [],
  clients = [],
  selectedChatId,
  onSelectChat,
  onSelectDoctor,
  onSelectClient,
  searchText,
  onSearchChange,
  activeTab,
  onTabChange,
  tabCounts = { internal: 0, external: 0 },
}) => {
  const counts = {
    internal: tabCounts.internal ?? 0,
    external: tabCounts.external ?? 0,
  };

  const isInternal = activeTab === "internal";

  const getAvatarBgColor = (initials = "") => {
    const colors = ["#E2F0FD", "#FFF4E5", "#EAF8E6", "#FCE8E6", "#EBE8FC", "#FCE8F3"];
    let hash = 0;
    for (let i = 0; i < initials.length; i++) {
      hash = initials.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getAvatarTextColor = (initials = "") => {
    const colors = ["#0054A6", "#B25E00", "#1E6B08", "#C5221F", "#5225B8", "#B80672"];
    let hash = 0;
    for (let i = 0; i < initials.length; i++) {
      hash = initials.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const renderChatRow = (chat) => {
    const isSelected = String(chat.id) === String(selectedChatId);
    const displayName = chat.petName
      ? `${chat.patientName} & ${chat.petName}`
      : chat.patientName;

    return (
      <Box
        key={chat.id}
        onClick={() => onSelectChat(chat.id)}
        sx={{
          display: "flex",
          alignItems: "center",
          p: "12px",
          mb: "8px",
          borderRadius: "12px",
          cursor: "pointer",
          backgroundColor: isSelected ? AppColors.lightBlue : "transparent",
          border: isSelected ? `1px solid #79C5EF40` : "1px solid transparent",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: isSelected ? AppColors.lightBlue : "#F8FAFC",
          },
        }}
      >
        <Box sx={{ position: "relative", mr: 1.5 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              backgroundColor: getAvatarBgColor(chat.avatarInitials),
              color: getAvatarTextColor(chat.avatarInitials),
              fontSize: "15px",
              fontWeight: "700",
              fontFamily: "Montserrat",
            }}
          >
            {chat.avatarInitials}
          </Avatar>
          {chat.isOnline && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#10C200",
                border: `2px solid ${AppColors.white}`,
              }}
            />
          )}
          {chat.channel === "whatsapp" && (
            <Box
              sx={{
                position: "absolute",
                top: -4,
                left: -4,
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#25D366",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1.5px solid ${AppColors.white}`,
              }}
            >
              <FaWhatsapp style={{ color: AppColors.white, fontSize: "10px" }} />
            </Box>
          )}
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              mb: 0.5,
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#1E293B",
                fontFamily: "Montserrat",
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: "#94A3B8",
                fontFamily: "Montserrat",
                whiteSpace: "nowrap",
                ml: 1,
              }}
            >
              {chat.lastMessageTime}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: "12px",
                color: isSelected ? "#334155" : "#64748B",
                fontFamily: "Montserrat",
                mr: 1,
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {chat.lastMessage}
            </Typography>
            {chat.unreadCount > 0 ? (
              <Box
                sx={{
                  backgroundColor: "#0054A6",
                  color: AppColors.white,
                  borderRadius: "50%",
                  width: 18,
                  height: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "700",
                  fontFamily: "Montserrat",
                  flexShrink: 0,
                }}
              >
                {chat.unreadCount}
              </Box>
            ) : (
              chat.channel === "whatsapp" && (
                <DoneAllIcon
                  sx={{ fontSize: "16px", color: "#3B82F6", flexShrink: 0 }}
                />
              )
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderDoctorRow = (doctor) => {
    const doctorId = doctor.doctorId ?? doctor.id;
    const name = doctor.doctorName || doctor.name || "Doctor";
    const initials = name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "DR";
    const relatedChatId = doctor.existingChatId;
    const isSelected =
      relatedChatId != null &&
      String(relatedChatId) === String(selectedChatId);

    return (
      <Box
        key={doctorId}
        onClick={() => onSelectDoctor?.(doctor)}
        sx={{
          display: "flex",
          alignItems: "center",
          p: "12px",
          mb: "8px",
          borderRadius: "12px",
          cursor: "pointer",
          backgroundColor: isSelected ? AppColors.lightBlue : "transparent",
          border: isSelected ? `1px solid #79C5EF40` : "1px solid transparent",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: isSelected ? AppColors.lightBlue : "#F8FAFC",
          },
        }}
      >
        <Avatar
          src={doctor.image || undefined}
          sx={{
            width: 44,
            height: 44,
            mr: 1.5,
            backgroundColor: getAvatarBgColor(initials),
            color: getAvatarTextColor(initials),
            fontSize: "15px",
            fontWeight: "700",
            fontFamily: "Montserrat",
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#1E293B",
              fontFamily: "Montserrat",
            }}
          >
            {name}
          </Typography>
          <Typography
            noWrap
            sx={{
              fontSize: "12px",
              color: "#64748B",
              fontFamily: "Montserrat",
              mt: 0.25,
            }}
          >
            {relatedChatId
              ? doctor.lastMessage || "Open conversation"
              : "Tap to start chat"}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: "600",
            color: "#94A3B8",
            fontFamily: "Montserrat",
            ml: 1,
          }}
        >
          ID {doctorId}
        </Typography>
      </Box>
    );
  };

  const listEmpty =
    chats.length === 0 &&
    (isInternal ? doctors.length === 0 : clients.length === 0);

  const renderClientRow = (client) => {
    const name = client.name || "Client";
    const initials =
      name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join("") || "C";
    const rowKey = client.id ?? client.mobile ?? name;
    return (
      <Box
        key={rowKey}
        onClick={() => onSelectClient?.(client)}
        sx={{
          display: "flex",
          alignItems: "center",
          p: "12px",
          mb: "8px",
          borderRadius: "12px",
          cursor: "pointer",
          backgroundColor: "transparent",
          border: "1px solid transparent",
          transition: "all 0.2s ease",
          "&:hover": { backgroundColor: "#F8FAFC" },
        }}
      >
        <Avatar
          src={client.image || undefined}
          sx={{
            width: 44,
            height: 44,
            mr: 1.5,
            backgroundColor: getAvatarBgColor(initials),
            color: getAvatarTextColor(initials),
            fontSize: "15px",
            fontWeight: "700",
            fontFamily: "Montserrat",
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#1E293B",
              fontFamily: "Montserrat",
            }}
          >
            {name}
          </Typography>
          <Typography
            noWrap
            sx={{
              fontSize: "12px",
              color: "#64748B",
              fontFamily: "Montserrat",
              mt: 0.25,
            }}
          >
            {client.mobile || "Tap to start WhatsApp chat"}
          </Typography>
        </Box>
        <FaWhatsapp style={{ color: "#25D366", fontSize: "18px", flexShrink: 0 }} />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid #E2E8F0",
        height: "100%",
        overflow: "hidden",
        backgroundColor: AppColors.white,
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {TAB_ORDER.map((tabKey) => {
            const isSelected = activeTab === tabKey;
            return (
              <Box
                key={tabKey}
                onClick={() => onTabChange(tabKey)}
                sx={{
                  cursor: "pointer",
                  pb: 1,
                  px: 0.5,
                  borderBottom: isSelected
                    ? `2px solid ${AppColors.appPrimary}`
                    : "2px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  transition: "all 0.2s ease",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: isSelected ? "700" : "500",
                    color: isSelected ? AppColors.appPrimary : AppColors.gray5,
                    fontFamily: "Montserrat",
                  }}
                >
                  {TAB_LABELS[tabKey]}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: isSelected
                      ? AppColors.appPrimary
                      : "#F1F5F9",
                    color: isSelected ? AppColors.white : AppColors.gray5,
                    borderRadius: "10px",
                    px: 0.8,
                    py: 0.1,
                    fontSize: "10px",
                    fontWeight: "600",
                    fontFamily: "Montserrat",
                  }}
                >
                  {counts[tabKey]}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ p: 2, display: "flex", gap: 1, alignItems: "center" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#F8FAFC",
            borderRadius: "20px",
            px: 2,
            py: 0.5,
            flexGrow: 1,
            border: "1px solid #E2E8F0",
          }}
        >
          <SearchIcon
            sx={{ color: AppColors.gray5, fontSize: "20px", mr: 1 }}
          />
          <InputBase
            placeholder={
              isInternal
                ? "Search staff to start a new chat"
                : "Search or start new chat"
            }
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              fontSize: "13px",
              fontFamily: "Montserrat",
              width: "100%",
              "& input::placeholder": {
                fontFamily: "Montserrat",
                color: "#94A3B8",
                opacity: 1,
              },
            }}
          />
        </Box>
        <IconButton
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: "10px",
            p: "8px",
            backgroundColor: "#F8FAFC",
            "&:hover": { backgroundColor: "#F1F5F9" },
          }}
        >
          <FilterListIcon sx={{ color: AppColors.gray5, fontSize: "20px" }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: 2,
        }}
      >
        {!listEmpty ? (
          <>
            {chats.map(renderChatRow)}
            {isInternal && doctors.length > 0 && (
              <>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#94A3B8",
                    fontFamily: "Montserrat",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                    mt: chats.length > 0 ? 1.5 : 0,
                    mb: 1,
                    px: 0.5,
                  }}
                >
                  Start new chat
                </Typography>
                {doctors.map(renderDoctorRow)}
              </>
            )}
            {!isInternal && clients.length > 0 && (
              <>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#94A3B8",
                    fontFamily: "Montserrat",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                    mt: chats.length > 0 ? 1.5 : 0,
                    mb: 1,
                    px: 0.5,
                  }}
                >
                  Start WhatsApp chat
                </Typography>
                {clients.map(renderClientRow)}
              </>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: "center", mt: 4, px: 2 }}>
            <Typography
              sx={{
                color: AppColors.gray5,
                fontSize: "14px",
                fontFamily: "Montserrat",
              }}
            >
              {isInternal
                ? "No internal chats yet. Search a staff member to start chatting."
                : "No chats found. Search a client to start a WhatsApp chat."}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatSidebar;
